import React, { useState } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  Bot, 
  Settings, 
  AlertTriangle, 
  Target,
  Timer,
  DollarSign,
  Zap
} from 'lucide-react';
import TradingChart from '../components/TradingChart';

const Trading: React.FC = () => {
  const [orderType, setOrderType] = useState<'market' | 'limit' | 'stop'>('market');
  const [tradeType, setTradeType] = useState<'buy' | 'sell'>('buy');
  const [amount, setAmount] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');
  const [aiEnabled, setAiEnabled] = useState(false);

  const currentPrice = 2038.90;
  const dailyChange = 8.40;
  const dailyChangePercent = 0.41;

  const orderTypes = [
    { value: 'market', label: 'Market Order', description: 'Execute immediately at current price' },
    { value: 'limit', label: 'Limit Order', description: 'Execute at specific price or better' },
    { value: 'stop', label: 'Stop Order', description: 'Execute when price reaches stop level' }
  ];

  const quickAmounts = ['0.5', '1.0', '2.5', '5.0'];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="win-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-win-dark-text mb-2">Gold Trading</h1>
            <p className="text-win-dark-text-muted">Trade gold with advanced tools and AI assistance</p>
          </div>
          
          {/* Live Price Display */}
          <div className="flex items-center gap-6">
            <div className="text-right">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-win-dark-text-muted">XAU/USD</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-win-dark-text">
                  ${currentPrice.toLocaleString()}
                </span>
                <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                  dailyChange >= 0 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {dailyChange >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  <span>{dailyChange >= 0 ? '+' : ''}${dailyChange} ({dailyChange >= 0 ? '+' : ''}{dailyChangePercent}%)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Trading Chart */}
        <div className="xl:col-span-3">
          <TradingChart />
        </div>

        {/* Trading Panel */}
        <div className="space-y-6">
          {/* Order Type Selector */}
          <div className="win-card p-6">
            <h3 className="text-lg font-bold text-win-dark-text mb-4">Place Order</h3>
            
            {/* Buy/Sell Toggle */}
            <div className="flex bg-win-dark-surface-light rounded-3xl p-1 mb-6">
              <button
                onClick={() => setTradeType('buy')}
                className={`flex-1 py-3 px-4 rounded-3xl font-medium transition-all duration-200 ${
                  tradeType === 'buy'
                    ? 'bg-green-500 text-white shadow-md'
                    : 'text-win-dark-text-muted hover:text-win-dark-text'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  <span>BUY</span>
                </div>
              </button>
              <button
                onClick={() => setTradeType('sell')}
                className={`flex-1 py-3 px-4 rounded-3xl font-medium transition-all duration-200 ${
                  tradeType === 'sell'
                    ? 'bg-red-500 text-white shadow-md'
                    : 'text-win-dark-text-muted hover:text-win-dark-text'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <TrendingDown className="w-4 h-4" />
                  <span>SELL</span>
                </div>
              </button>
            </div>

            {/* Order Type Selection */}
            <div className="space-y-3 mb-6">
              {orderTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setOrderType(type.value as any)}
                  className={`w-full p-4 rounded-3xl border text-left transition-all duration-200 ${
                    orderType === type.value
                      ? 'border-win-dark-accent bg-win-dark-accent/10'
                      : 'border-win-dark-border hover:border-win-dark-accent/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-win-dark-text">{type.label}</p>
                      <p className="text-sm text-win-dark-text-muted">{type.description}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 ${
                      orderType === type.value
                        ? 'border-win-dark-accent bg-win-dark-accent'
                        : 'border-win-dark-border'
                    }`}>
                      {orderType === type.value && (
                        <div className="w-full h-full rounded-full bg-white scale-50"></div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Amount Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-win-dark-text mb-2">
                Amount (oz)
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="win-input w-full"
              />
              <div className="flex gap-2 mt-2">
                {quickAmounts.map((qty) => (
                  <button
                    key={qty}
                    onClick={() => setAmount(qty)}
                    className="flex-1 py-2 px-3 bg-win-dark-surface-light hover:bg-win-dark-border rounded-2xl text-sm font-medium text-win-dark-text transition-colors"
                  >
                    {qty} oz
                  </button>
                ))}
              </div>
            </div>

            {/* Limit Price (for limit/stop orders) */}
            {(orderType === 'limit' || orderType === 'stop') && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-win-dark-text mb-2">
                  {orderType === 'limit' ? 'Limit Price' : 'Stop Price'} ($)
                </label>
                <input
                  type="number"
                  value={limitPrice}
                  onChange={(e) => setLimitPrice(e.target.value)}
                  placeholder={`Enter ${orderType} price`}
                  className="win-input w-full"
                />
              </div>
            )}

            {/* Risk Management */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-win-dark-text mb-2">
                  Stop Loss ($)
                </label>
                <input
                  type="number"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                  placeholder="Optional"
                  className="win-input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-win-dark-text mb-2">
                  Take Profit ($)
                </label>
                <input
                  type="number"
                  value={takeProfit}
                  onChange={(e) => setTakeProfit(e.target.value)}
                  placeholder="Optional"
                  className="win-input w-full"
                />
              </div>
            </div>

            {/* AI Trading Toggle */}
            <div className="flex items-center justify-between p-4 bg-win-dark-surface-light rounded-3xl mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-win-dark-accent/20 rounded-2xl">
                  <Bot className="w-5 h-5 text-win-dark-accent" />
                </div>
                <div>
                  <p className="font-medium text-win-dark-text">AI Assistance</p>
                  <p className="text-sm text-win-dark-text-muted">Let AI optimize your trade</p>
                </div>
              </div>
              <button
                onClick={() => setAiEnabled(!aiEnabled)}
                className={`w-12 h-6 rounded-full transition-colors duration-200 ${
                  aiEnabled ? 'bg-win-dark-accent' : 'bg-win-dark-border'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                  aiEnabled ? 'translate-x-6' : 'translate-x-0.5'
                }`}></div>
              </button>
            </div>

            {/* Order Summary */}
            <div className="p-4 bg-win-dark-surface-light rounded-3xl mb-4">
              <h4 className="font-medium text-win-dark-text mb-3">Order Summary</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-win-dark-text-muted">Type:</span>
                  <span className="text-win-dark-text capitalize">{tradeType} {orderType}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-win-dark-text-muted">Amount:</span>
                  <span className="text-win-dark-text">{amount || '0'} oz</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-win-dark-text-muted">Price:</span>
                  <span className="text-win-dark-text">
                    ${orderType === 'market' ? currentPrice.toLocaleString() : (limitPrice || '0')}
                  </span>
                </div>
                <div className="flex justify-between border-t border-win-dark-border pt-2">
                  <span className="text-win-dark-text-muted">Total:</span>
                  <span className="text-win-dark-text font-medium">
                    ${((parseFloat(amount) || 0) * (orderType === 'market' ? currentPrice : parseFloat(limitPrice) || 0)).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Place Order Button */}
            <button className={`w-full py-4 rounded-3xl font-bold text-white transition-all duration-200 ${
              tradeType === 'buy'
                ? 'bg-green-500 hover:bg-green-600 shadow-lg hover:shadow-xl'
                : 'bg-red-500 hover:bg-red-600 shadow-lg hover:shadow-xl'
            }`}>
              <div className="flex items-center justify-center gap-2">
                {tradeType === 'buy' ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
                <span>{tradeType.toUpperCase()} GOLD</span>
              </div>
            </button>
          </div>

          {/* Account Info */}
          <div className="win-card p-6">
            <h3 className="text-lg font-bold text-win-dark-text mb-4">Account Info</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-win-dark-text-muted">Available Balance</span>
                <span className="font-bold text-win-dark-text">$15,750.00</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-win-dark-text-muted">Margin Used</span>
                <span className="font-bold text-win-dark-text">$3,250.00</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-win-dark-text-muted">Free Margin</span>
                <span className="font-bold text-green-400">$12,500.00</span>
              </div>
              <div className="flex items-center justify-between border-t border-win-dark-border pt-4">
                <span className="text-win-dark-text-muted">Trades Today</span>
                <span className="font-bold text-win-dark-text">12/100</span>
              </div>
            </div>
          </div>

          {/* Risk Warning */}
          <div className="win-card p-4 bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5" />
              <div>
                <p className="font-medium text-orange-400 mb-1">Risk Warning</p>
                <p className="text-sm text-win-dark-text-muted">
                  Trading gold involves significant risk. Only trade with funds you can afford to lose.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Trading;